import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import './BlendSearch.scss';

import FeedCard from './FeedCard';
import ShopmyImage from '../General/ShopmyImage';
import { fetchHomeBlendedFeed } from '../../api/shop';
import { fetchUserFromUsername } from '../../api/users';

const PAGE_SIZE = 24;

const BlendSearch = () => {
  const history = useHistory();
  const [usernameInput, setUsernameInput] = useState('');
  const [user, setUser] = useState(null);
  const [pages, setPages] = useState([]);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  // Latched on a failed page fetch so the sentinel stops re-firing until the user retries or resubmits.
  const [pageLoadFailed, setPageLoadFailed] = useState(false);
  const sentinelRef = useRef(null);
  // Bumped on every new lookup so a superseded username's in-flight feed pages can't land.
  const feedSessionRef = useRef(0);

  const loadFeedPage = useCallback((feedUser, pageIndex) => {
    const session = feedSessionRef.current;
    setPageLoadFailed(false);
    setIsLoadingPage(true);
    fetchHomeBlendedFeed({ User_id: feedUser.id, page: pageIndex, limit: PAGE_SIZE })
      .then(feedPage => session === feedSessionRef.current && setPages(previous => [...previous, feedPage]))
      .catch(() => {
        if (session !== feedSessionRef.current) return;
        setPageLoadFailed(true);
        window.ALERT.error(pageIndex ? 'Failed to load more of the feed.' : 'Failed to load the home feed — please try again.');
      })
      .finally(() => session === feedSessionRef.current && setIsLoadingPage(false));
  }, []);

  // Resolve a typed username to its user (the app's deep-link resolver), then load page 0 of that
  // user's home feed. Runs on every submit, so re-entering the same username reloads the feed.
  const loadFeedForUsername = useCallback(
    rawUsername => {
      const username = rawUsername.trim().replace(/^@/, '');
      if (!username) return;
      const session = ++feedSessionRef.current;
      setUsernameInput(username);
      setIsLoadingUser(true);
      fetchUserFromUsername(username)
        .then(feedUser => {
          if (session !== feedSessionRef.current) return;
          if (!feedUser?.id) {
            // A failed lookup keeps the previous feed on screen — clear the page-loading flag the
            // session bump orphaned so that feed's sentinel re-arms.
            setIsLoadingPage(false);
            return window.ALERT.error(`No ShopMy user with the username @${username}.`);
          }
          setUser(feedUser);
          setUsernameInput(feedUser.username);
          history.replace({ search: `?username=${encodeURIComponent(feedUser.username)}` });
          setPages([]);
          loadFeedPage(feedUser, 0);
        })
        .catch(() => {
          if (session !== feedSessionRef.current) return;
          setIsLoadingPage(false);
          window.ALERT.error(`Could not look up @${username} — please try again.`);
        })
        .finally(() => session === feedSessionRef.current && setIsLoadingUser(false));
    },
    [history, loadFeedPage]
  );

  useEffect(() => {
    const initialUsername = new URLSearchParams(history.location.search).get('username');
    if (initialUsername) loadFeedForUsername(initialUsername);
  }, [history, loadFeedForUsername]);

  // The blended list is the app's authoritative rank order — join each entry to its hydrated
  // product/collection by type + id, exactly as the app does.
  const cards = useMemo(() => {
    const seenKeys = new Set();
    const joined = [];
    pages.forEach(page => {
      const productsById = _.keyBy(page.products || [], product => Number(product.id));
      const collectionsById = _.keyBy(page.collections || [], collection => Number(collection.id));
      (page.blended || []).forEach(entry => {
        const key = `${entry.type}:${entry.id}`;
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        const data = entry.type === 'product' ? productsById[Number(entry.id)] : collectionsById[Number(entry.id)];
        if (data) joined.push({ key, type: entry.type, blendScore: entry.blendScore, data });
      });
    });
    return joined;
  }, [pages]);

  const hasMore = !!pages.length && !!pages[pages.length - 1].hasMoreBlended;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !user || !hasMore || isLoadingPage || pageLoadFailed) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadFeedPage(user, pages.length);
      },
      { rootMargin: '600px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [user, pages, hasMore, isLoadingPage, pageLoadFailed, loadFeedPage]);

  const retryButton = (
    <button type='button' className='blend-retry-button' onClick={() => loadFeedPage(user, pages.length)}>
      Retry
    </button>
  );

  const productCount = cards.filter(card => card.type === 'product').length;
  const collectionCount = cards.length - productCount;

  return (
    <div className='blend-search-outer'>
      <div className='blend-search-header'>
        <div className='blend-search-title'>Blend Search</div>
        <div className='blend-search-subtitle'>
          The app's live home feed — For You products and recommended collections, rank-blended by the server exactly as the app renders them. Type a
          ShopMy username to preview the feed that user sees.
        </div>
      </div>

      <form
        className='blend-user-form'
        onSubmit={event => {
          event.preventDefault();
          loadFeedForUsername(usernameInput);
        }}
      >
        <input
          type='text'
          value={usernameInput}
          onChange={({ target }) => setUsernameInput(target.value)}
          placeholder='Type a ShopMy username, then press Enter'
        />
        <button type='submit' disabled={isLoadingUser}>
          {isLoadingUser ? 'Looking up...' : 'Preview Feed'}
        </button>
      </form>

      {user && (
        <div className='blend-user-row'>
          <div className='blend-user-chip'>
            {user.image ? <ShopmyImage source={user.image} alt={user.name} /> : <div className='chip-avatar-placeholder' />}
            <div className='chip-names'>
              <div className='chip-name'>{user.name || `@${user.username}`}</div>
              <div className='chip-username'>
                @{user.username} · User #{user.id}
              </div>
            </div>
          </div>
          {!!cards.length && (
            <div className='blend-feed-mix'>
              {productCount} products · {collectionCount} collections loaded
            </div>
          )}
        </div>
      )}

      {!user && <div className='blend-search-empty'>Enter a username to preview their home feed.</div>}
      {user && !pages.length && isLoadingPage && <div className='blend-search-empty'>Loading feed...</div>}
      {user && !pages.length && pageLoadFailed && (
        <div className='blend-search-empty'>
          Could not load the home feed for @{user.username}. {retryButton}
        </div>
      )}
      {user && !!pages.length && !cards.length && (
        <div className='blend-search-empty'>This feed came back empty — the recommendation services have nothing for @{user.username}.</div>
      )}
      {!!cards.length && (
        <>
          <div className='blend-search-grid'>
            {cards.map((card, index) => (
              <FeedCard key={card.key} card={card} rank={index + 1} />
            ))}
          </div>
          <div ref={sentinelRef} className='blend-search-sentinel'>
            {!hasMore && 'End of feed'}
            {hasMore && !pageLoadFailed && 'Loading more...'}
            {hasMore && pageLoadFailed && <>Could not load more of the feed. {retryButton}</>}
          </div>
        </>
      )}
    </div>
  );
};

export default BlendSearch;
